import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import {
  Activity, Map, Building2, BookOpen, IndianRupee, Briefcase, Image as ImageIcon,
  AlertTriangle, CheckCircle2, Loader2, Gauge,
} from "lucide-react";

export default function AdminDataHealth() {
  const [h, setH] = useState(null);
  useEffect(() => { api.get("/admin/mid/health").then((r) => setH(r.data)).catch(() => setH(false)); }, []);

  if (h === null) return <div className="flex justify-center py-32"><Loader2 className="h-10 w-10 animate-spin text-[#0B2A6B]" /></div>;
  if (h === false || (h.totals?.colleges ?? 0) === 0)
    return (
      <div data-testid="health-empty" className="space-y-4">
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Activity className="h-6 w-6 text-[#0B2A6B]" /> Data Health</h1>
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">No MID data yet. Import a workbook to populate the database.</div>
      </div>
    );

  const t = h.totals;
  const score = h.dataQualityScore;
  const scoreColor = score >= 70 ? "#10B981" : score >= 40 ? "#D4A24C" : "#EF4444";

  const totals = [
    { k: "States", v: t.states, icon: Map, c: "#0B2A6B" },
    { k: "Cities", v: t.cities, icon: Map, c: "#6366F1" },
    { k: "Colleges", v: t.colleges, icon: Building2, c: "#0EA5E9" },
    { k: "Courses", v: t.courses, icon: BookOpen, c: "#8B5CF6" },
    { k: "Fees", v: t.fees, icon: IndianRupee, c: "#10B981" },
    { k: "Placements", v: t.placements, icon: Briefcase, c: "#D4A24C" },
    { k: "Images", v: t.images, icon: ImageIcon, c: "#EC4899" },
    { k: "Sources", v: t.sources, icon: CheckCircle2, c: "#0B2A6B" },
  ];

  return (
    <div data-testid="admin-data-health-page" className="space-y-8">
      <SEOHead title="Data Health" />
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Activity className="h-6 w-6 text-[#0B2A6B]" /> Data Health Dashboard</h1>
        <p className="text-sm text-slate-500">Live integrity & coverage of the MID database.</p>
      </div>

      {/* Quality score + flags */}
      <div className="grid lg:grid-cols-4 gap-4">
        <div data-testid="health-quality-score" className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 text-sm text-slate-500"><Gauge className="h-4 w-4" /> Data Quality Score</div>
          <p className="mt-2 font-display text-4xl font-extrabold" style={{ color: scoreColor }}>{score}%</p>
          <div className="mt-3 h-2.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${score}%`, background: scoreColor }} /></div>
          <p className="mt-2 text-xs text-slate-400">Weighted by verified fees, placements, images & status.</p>
        </div>
        <Flag testid="health-pending-fees" label="Colleges · Pending Fees" value={h.pending.fees} icon={IndianRupee} />
        <Flag testid="health-pending-placements" label="Colleges · Pending Placements" value={h.pending.placements} icon={Briefcase} />
        <Flag testid="health-duplicates" label="Duplicate Institutions" value={h.duplicates} icon={AlertTriangle} danger={h.duplicates > 0} />
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {totals.map((x) => (
          <div key={x.k} data-testid={`health-total-${x.k.toLowerCase()}`} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl" style={{ background: `${x.c}1a` }}><x.icon className="h-5 w-5" style={{ color: x.c }} /></span>
            <p className="mt-3 font-display text-3xl font-extrabold text-slate-900">{x.v}</p>
            <p className="text-sm text-slate-500">{x.k}</p>
          </div>
        ))}
      </div>

      {/* Last import */}
      {h.lastImport && (
        <div data-testid="health-last-import" className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm text-sm">
          <h3 className="font-display font-bold text-slate-900 mb-2">Last Import</h3>
          <div className="flex flex-wrap gap-x-8 gap-y-1 text-slate-600">
            <span>Version: <b className="text-slate-900">v{h.lastImport.version}</b></span>
            <span>File: <b className="text-slate-900">{h.lastImport.filename}</b></span>
            <span>Duration: <b className="text-slate-900">{(h.lastImport.durationMs/1000).toFixed(1)}s</b></span>
            <span>When: <b className="text-slate-900">{new Date(h.lastImport.finishedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</b></span>
          </div>
        </div>
      )}

      {/* Coverage */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Coverage title="State-wise Coverage" rows={h.stateCoverage} keyField="state" testid="health-state-coverage" />
        <Coverage title="City-wise Coverage (top 40)" rows={h.cityCoverage} keyField="city" testid="health-city-coverage" />
      </div>
    </div>
  );
}

function Flag({ label, value, icon: Icon, danger, testid }) {
  return (
    <div data-testid={testid} className={`rounded-2xl border bg-white p-6 shadow-sm ${danger ? "border-red-200" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 text-sm text-slate-500"><Icon className="h-4 w-4" /> {label}</div>
      <p className={`mt-2 font-display text-4xl font-extrabold ${danger ? "text-red-600" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

function Coverage({ title, rows, keyField, testid }) {
  const max = Math.max(1, ...(rows || []).map((r) => r.colleges));
  return (
    <div data-testid={testid} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-3 font-display font-bold text-slate-900">{title}</h3>
      <div className="space-y-2 max-h-96 overflow-auto">
        {(rows || []).map((r) => (
          <div key={r[keyField]}>
            <div className="flex justify-between text-sm"><span className="text-slate-600 truncate pr-2">{r[keyField]}</span><span className="font-semibold text-slate-900">{r.colleges}</span></div>
            <div className="mt-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-[#0B2A6B]" style={{ width: `${(r.colleges/max)*100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}
